import React, { useEffect } from 'react';

import { capitalize } from 'lodash-es';

import { DateTime, Duration } from 'luxon';

import { Box, COLORS, FontWeight, Text, TextButton } from '@clutter/clean';

import { Source } from '@graphql/platform';

import { usePaymentContext } from '@root/components/checkout/payment_context';
import { EventSchema } from '@root/resources/wt/types';

import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';

import checkCircle from '@images/icons/check-circle.svg';

import { CreditCardInput } from './credit_card_input';
import { CredibilityBanner } from './subcomponents/credibility_banner';

const SavedPaymentMethod: React.FC<{
  onRemove(): void;
  source: Source;
}> = ({ onRemove, source }) => (
  <Box.Flex
    boxShadow="0px 4px 12px rgba(0, 0, 0, 0.1)"
    borderRadius="4px"
    padding="24px"
  >
    <Box.FlexItem alignSelf="center" padding="0 16px 0 0">
      <img src={checkCircle} alt="" />
    </Box.FlexItem>
    <Box.FlexItem>
      <Text.Body weight={FontWeight.Medium}>Saved payment method</Text.Body>
      <Text.Body>
        {source.fundingType && <>{capitalize(source.fundingType)} Card </>}
        {`**** **** **** ${source.number ?? '****'}`}
      </Text.Body>
    </Box.FlexItem>
    <Box.FlexItem alignSelf="center" margin="0 0 0 auto">
      <TextButton kind="destructive" onClick={onRemove}>
        Remove
      </TextButton>
    </Box.FlexItem>
  </Box.Flex>
);

type PaymentMethodProps = {
  movingDepositInfo?: {
    interval: Duration;
    formattedInterval: string;
    appointmentDateTime: DateTime;
  };
  disabled?: boolean;
  defaultSource?: Source;
  onClearDefaultSource(): void;
};

export const PaymentMethod: React.FC<PaymentMethodProps> = ({
  movingDepositInfo,
  disabled,
  defaultSource,
  onClearDefaultSource,
}) => {
  const trackFunnelEvent = useTrackFunnelEvents();

  const {
    billingName,
    billingNameError,
    onBillingNameChange,
    onCardInputChange,
    paymentMethodError,
  } = usePaymentContext();

  useEffect(() => {
    trackFunnelEvent({
      schema: EventSchema.WWW__PaymentMethodViewed,
      action: 'display',
      metadata: {},
    });
  }, [trackFunnelEvent]);

  const onRemove = () => {
    if (!disabled) onClearDefaultSource();
  };

  const subText =
    movingDepositInfo &&
    movingDepositInfo.appointmentDateTime <=
      DateTime.now().plus(movingDepositInfo.interval)
      ? `Since you are scheduling a move that takes place within ${movingDepositInfo.formattedInterval}, your deposit will be charged today.`
      : 'You won’t be charged now, but we require a payment method to hold your appointment.';

  return (
    <Box>
      <Box margin="0 0 16px">
        <Box margin="0 0 4px">
          <Text.Title size="extraSmall">Add payment method</Text.Title>
        </Box>
        <Text.Callout color={COLORS.storm}>{subText}</Text.Callout>
      </Box>
      {defaultSource ? (
        <SavedPaymentMethod onRemove={onRemove} source={defaultSource} />
      ) : (
        <>
          <Box margin="24px 0 0">
            <CreditCardInput
              disabled={disabled}
              billingName={billingName}
              onChangeBillingName={onBillingNameChange}
              onChangeCard={onCardInputChange}
              paymentError={paymentMethodError}
              billingNameError={billingNameError}
            />
          </Box>
          <CredibilityBanner showCardIcons />
        </>
      )}
    </Box>
  );
};
